import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';

/** Uma linha (produto) da comparação — campos crus de cada lado ficam em `campos` (nome da coluna -> valor em texto). */
export interface ComparacaoEstoqueLado {
    existe: boolean;
    qtdeTotal?: number;
    /** Quantas linhas (registros) desse lado foram somadas pra formar qtdeTotal (>1 = mais de um local/lote pro mesmo produto). */
    linhas?: number;
    campos?: Record<string, string>;
}

/** Critério usado pra decidir "divergente" -- ver comentário em ComparacaoEstoqueComponent (backend). */
export type TipoComparacao = 'ESTOQUE' | 'VALOR' | 'ATIVO' | 'PN' | 'LOCAL' | 'SERIE' | 'LOTE';

export interface ComparacaoEstoqueItem {
    /** APE_ID (Aerosys) / CODPROD (Sankhya) — a mesma chave nos dois lados. */
    codigo: string;
    /** @deprecated usar divergenteEstoque -- mantido só por compatibilidade, é o mesmo valor. */
    divergente: boolean;
    divergenteEstoque: boolean;
    divergenteValor: boolean;
    divergenteAtivo: boolean;
    divergentePn: boolean;
    divergenteLocal: boolean;
    divergenteSerie: boolean;
    divergenteLote: boolean;
    /** TIPCONTEST do produto (TGFPRO) -- 'E'=série, 'L'=lote/form, 'N'=sem controle. */
    tipoControle: string;
    /** true só quando o produto é serial-controlado E as contagens estavam disponíveis (empresas selecionadas). */
    aplicaSerie: boolean;
    /** true só quando o produto é lote-controlado E as contagens estavam disponíveis. */
    aplicaLote: boolean;
    qtdeSerieSankhya?: number;
    qtdeSerieAerosys?: number;
    qtdeLoteSankhya?: number;
    qtdeLoteAerosys?: number;
    qtdeAerosys: number | null;
    qtdeSankhya: number | null;
    aerosys: ComparacaoEstoqueLado;
    sankhya: ComparacaoEstoqueLado;
}

export interface ComparacaoEstoqueResposta {
    linhas: ComparacaoEstoqueItem[];
    total: number;
    /** @deprecated usar totalDivergentesEstoque. */
    totalDivergentes: number;
    totalDivergentesEstoque: number;
    totalDivergentesValor: number;
    totalDivergentesAtivo: number;
    totalDivergentesPn: number;
    totalDivergentesLocal: number;
    totalDivergentesSerie: number;
    totalDivergentesLote: number;
    /** false = série/lote não foram calculados nesta busca (precisa empresa Aerosys E Sankhya específicas selecionadas). */
    contagemSerieLoteDisponivel: boolean;
    /** Presente só quando contagemSerieLoteDisponivel=false por causa de um erro (ex.: Aerosys fora do ar) -- ausência de motivo = simplesmente faltou selecionar as empresas. */
    contagemSerieLoteErro?: string;
    /** Nomes de coluna disponíveis do lado Aerosys, pra montar o seletor de colunas. */
    camposAerosys: string[];
    /** Nomes de coluna disponíveis do lado Sankhya, pra montar o seletor de colunas. */
    camposSankhya: string[];
}

/**
 * Tela de Comparação de Estoque (Aerosys x Sankhya) — ver comentário de topo
 * em ComparacaoEstoqueComponent (backend, pacote comparacaoestoque) para o
 * contexto geral. Diferente do padrão "fonte única" do resto do app: aqui as
 * DUAS fontes são buscadas e casadas juntas, de propósito.
 */
@Injectable({providedIn: 'root'})
export class ComparacaoEstoqueService {
    private readonly apiUrl = '/api/comparacao-estoque';

    constructor(private readonly http: HttpClient) {
    }

    /** empIdAerosys e codEmpSankhya são independentes — omitidos/null = todas as empresas daquele lado. */
    comparar(empIdAerosys: number | null, codEmpSankhya: string | null): Observable<ComparacaoEstoqueResposta> {
        let params = new HttpParams();
        if (empIdAerosys != null) params = params.set('empIdAerosys', String(empIdAerosys));
        if (codEmpSankhya) params = params.set('codEmpSankhya', codEmpSankhya);
        return this.http.get<ComparacaoEstoqueResposta>(`${this.apiUrl}/itens`, {params});
    }

    /**
     * TIPCONTEST (TGFPRO, independente de TGFEST) por CODPROD -- usado pela Nota
     * de Correção Fase 2 antes de gerar a nota, pra saber quais itens são
     * serial-controlados ('E'). Retorna um mapa { "CODPROD": "TIPCONTEST" }.
     */
    buscarTipoControle(codProds: string[]): Observable<Record<string, string>> {
        if (!codProds.length) return new Observable(sub => { sub.next({}); sub.complete(); });
        const params = new HttpParams().set('codProds', codProds.join(','));
        return this.http.get<Record<string, string>>(`${this.apiUrl}/tipo-controle`, {params});
    }

    /**
     * Seriais disponíveis no Aerosys (ICS_STATUS='N') por CODPROD, pra UMA
     * empresa Aerosys específica -- usado pela Nota de Correção Fase 2 pra
     * atribuir TGFSER aos itens serial-controlados selecionados. Retorna um
     * mapa { "CODPROD": [{icsId, icsNs, icoFormNumero, icoFormLote, icoId}] }.
     */
    buscarSeriaisDisponiveis(codProds: string[], empIdAerosys: number): Observable<Record<string, any[]>> {
        if (!codProds.length) return new Observable(sub => { sub.next({}); sub.complete(); });
        const params = new HttpParams()
            .set('codProds', codProds.join(','))
            .set('empIdAerosys', String(empIdAerosys));
        return this.http.get<Record<string, any[]>>(`${this.apiUrl}/seriais-disponiveis`, {params});
    }

    /**
     * Saldo de FORM/lote disponível no Aerosys (ICO_QUANT líquido de saídas já
     * comprometidas) por CODPROD, pra UMA empresa Aerosys específica, em ordem
     * FIFO -- usado pela Nota de Correção Fase 3 (lote/form). Retorna um mapa
     * { "CODPROD": [{icoId, icoFormNumero, icoFormLote, saldo}] }.
     */
    buscarFormsDisponiveis(codProds: string[], empIdAerosys: number): Observable<Record<string, any[]>> {
        if (!codProds.length) return new Observable(sub => { sub.next({}); sub.complete(); });
        const params = new HttpParams()
            .set('codProds', codProds.join(','))
            .set('empIdAerosys', String(empIdAerosys));
        return this.http.get<Record<string, any[]>>(`${this.apiUrl}/forms-disponiveis`, {params});
    }

    /**
     * Documentos (PDF via ICO_FORM_CAMINHO, Aerosys) disponíveis por CODPROD --
     * usado pra sugerir/anexar o documento de origem da compra na Nota de
     * Correção. Retorna um mapa { "CODPROD": [{icoId, formNumero, formLote,
     * formData, caminho, formStatus, formInvoice, cprNumTitulo, cprStatus,
     * cprDataEmissao}] }, mais recente primeiro. Sem empIdAerosys de propósito
     * (documento não é preso a uma empresa específica).
     */
    buscarDocumentosDisponiveis(codProds: string[]): Observable<Record<string, any[]>> {
        if (!codProds.length) return new Observable(sub => { sub.next({}); sub.complete(); });
        const params = new HttpParams().set('codProds', codProds.join(','));
        return this.http.get<Record<string, any[]>>(`${this.apiUrl}/documentos-disponiveis`, {params});
    }

    /** Bytes do PDF (icoId) pra preview em tela -- Blob, não JSON. */
    baixarDocumentoPdf(icoId: number): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/documento-pdf/${icoId}`, {responseType: 'blob'});
    }
}
